import _ from 'lodash';
import Bugsnag from '@bugsnag/js';
import request from '../lib/graphql-request';
import {
  businessModel,
  paginatedAddressModel,
} from '../data/business-registry/query';
import { UCSAddress, UCSBusiness } from '../types/ucs';
import { providedServices as legacyProvidedServices } from '../data/filters/provided-services';
import {
  CompanyTypeOptions,
  ConstructionSectorFilters,
} from '../data/filters/filters';

type LegacyFilterNames =
  | 'trades'
  | 'constructionSectors'
  | 'companyTypes'
  | 'classifications';
const legacyFilterMappping = {
  trades: legacyProvidedServices,
  constructionSectors: _.chain(ConstructionSectorFilters).keyBy('key').value(),
  companyTypes: _.chain(CompanyTypeOptions).keyBy('key').value(),
  classifications: {},
};

const getBusinessesById = async (ids: string[]): Promise<UCSBusiness[]> => {
  const query = `
    query {
      businessesByIds(
        ids: [${ids.join(',')}]
      ) {
        ${businessModel}
      }
    }
  `;
  const results: any = await request(query);
  if (
    !results?.businessesByIds ||
    results.businessesByIds.length < ids.length
  ) {
    const missed = [];
    results.businessesByIds.forEach((b) => {
      if (!ids.includes(b.id)) {
        missed.push(b.id);
      }
    });
    Bugsnag.notify(
      new Error(
        `Could not resolve all results from Business Registry!  Returned ${
          results.businessesByIds.length
        }, requested ${ids.length} - ID's missing: ${missed.toString()}`
      )
    );
  }
  return results.businessesByIds;
};

const getAddresses = async (businessId: string): Promise<UCSAddress[]> => {
  let query = `
    query {
      getAddressesByBusinessId(
        businessId: "${businessId}"
      ) {
        ${paginatedAddressModel}
      }
    }
  `;
  let results: any = await request(query);
  /* istanbul ignore next */
  const addresses =
    results?.getAddressesByBusinessId?.edges.map((e) => e.node) || [];
  /* istanbul ignore next */
  while (results?.getAddressesByBusinessId?.pageInfo?.hasNextPage) {
    query = `
    query {
      getAddressesByBusinessId(
        businessId: "${businessId}",
        after: ${results?.getAddressesByBusinessId?.pageInfo?.endCursor}
      ) {
        ${paginatedAddressModel}
      }
    }
  `;
    // https://eslint.org/docs/latest/rules/no-await-in-loop#when-not-to-use-it
    // eslint-disable-next-line no-await-in-loop
    results = await request(query);
    addresses.push(
      results?.getAddressesByBusinessId?.edges.map((e) => e.node) || []
    );
  }
  return addresses;
};

const termMapping = {
  and: 'and',
  hvac: 'HVAC',
};

export const translateKeyToDefaultLabel = (
  key: string,
  filterKey: LegacyFilterNames | ''
): string => {
  return (
    legacyFilterMappping[filterKey]?.[key]?.label ??
    key
      .split('_')
      .map((k) => termMapping[k] || k.charAt(0).toUpperCase() + k.slice(1))
      .join(' ')
      .trim()
  );
};

const toUrlKey = (key: string, filterKey: LegacyFilterNames) => {
  return (
    legacyFilterMappping[filterKey]?.[key]?.urlKey ??
    key.replace(/_/g, '-').toLowerCase()
  );
};

const queryBR: any = async (query, locale) => {
  return request(
    `
  query {
    ${query}
  }
`,
    false,
    {
      'Accept-Language': locale,
    }
  );
};

const requestProvidedServices = async (
  level: 'trade' | 'specialty' | 'category',
  locale: string
) => {
  return queryBR(
    `providedServicesByLevel(level: "${level}") {
    id
    key
    level
    path
  }`,
    locale
  )
    .then((result) => result?.providedServicesByLevel || [])
    .then((providedServices) =>
      providedServices.map((ps) => ({
        ...ps,
        type: 'trade',
        urlKey: toUrlKey(ps.key, 'trades'),
        label: translateKeyToDefaultLabel(ps.key, 'trades'),
      }))
    );
};

export interface Filter {
  key: string;
  label: string;
  urlKey: string;
}

interface ProvidedService extends Filter {
  id: string;
  level: 'trade' | 'specialty';
  type: 'trade';
}

export interface ProvidedServiceTrades extends ProvidedService {
  specialties: Array<ProvidedService>;
  tradeKey?: ProvidedService;
}

export interface Classification extends Filter {
  abbreviation: string;
  name: string;
  type: 'classifications';
}

interface ConstructionSector extends Filter {
  type: 'constructionSector';
}

interface CompanyType extends Filter {
  type: 'companyType';
}

export interface FiltersLists {
  constructionSectors: Array<ConstructionSector>;
  trades: Array<ProvidedServiceTrades>;
  classifications: Array<Classification>;
  companyTypes: Array<CompanyType>;
}

let filtersListsCache: FiltersLists;

export const clearFiltersCache = () => {
  filtersListsCache = null;
};

export const requestTrades = async (locale: string) => {
  if (filtersListsCache?.trades) {
    return filtersListsCache.trades;
  }

  return Promise.all([
    requestProvidedServices('trade', locale),
    requestProvidedServices('specialty', locale),
  ]).then(([trades, specialties]) => {
    return trades
      .map((t) => ({
        ...t,
        specialties: specialties
          .filter((s) => s.path.includes(`#${t.key}#`))
          .map(({ path, ...s }) => ({
            ...s,
            tradeKey: t.key,
          })),
      }))
      .map(({ path, ...rest }) => rest)
      .sort((a, b) => a.label.localeCompare(b.label));
  });
};

export const classificationsLabel = (c) => {
  return c.abbreviation ? `${c.name} (${c.abbreviation})` : c.name;
};

const requestClassifications = async (locale: string) => {
  return queryBR(
    `classificationTypes {
      abbreviation
      name
      key
    }`,
    locale
  )
    .then((result) => result?.classificationTypes || [])
    .then((classifications) =>
      classifications
        .map((c) => ({
          ...c,
          type: 'classifications',
          urlKey: toUrlKey(c.key, 'classifications'),
          label: classificationsLabel(c),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );
};

const requestCompanyTypes = async (locale: string) => {
  return queryBR(
    `businessTypes {
      key
    }`,
    locale
  )
    .then((result) => result?.businessTypes || [])
    .then((businessTypes) =>
      businessTypes
        .map((bt) => ({
          ...bt,
          type: 'companyType',
          urlKey: toUrlKey(bt.key, 'companyTypes'),
          label: translateKeyToDefaultLabel(bt.key, 'companyTypes'),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );
};

const requestConstructionSectors = async (locale: string) => {
  return queryBR(
    `constructionSectors {
    key
  }`,
    locale
  )
    .then((result) => result?.constructionSectors || [])
    .then((sectors) =>
      sectors
        .map((cs) => ({
          ...cs,
          urlKey: toUrlKey(cs.key, 'constructionSectors'),
          type: 'constructionSector',
          label: translateKeyToDefaultLabel(cs.key, 'constructionSectors'),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );
};

export const getFiltersLists = async (locale?: string) => {
  // eslint-disable-next-line no-param-reassign
  locale = locale || 'en-US';
  if (filtersListsCache) {
    return filtersListsCache;
  }

  const [trades, classifications, companyTypes, constructionSectors] =
    await Promise.all([
      requestTrades(locale),
      requestClassifications(locale),
      requestCompanyTypes(locale),
      requestConstructionSectors(locale),
    ]);

  filtersListsCache = {
    trades,
    classifications,
    companyTypes,
    constructionSectors,
  };
  return filtersListsCache;
};

export { getBusinessesById, getAddresses };
